import * as yup from 'yup';
import { ScopeableType } from '../../../types';
import { checkRevenueShareOverlap } from '../../../utils/discount-utils';
import { ScopeItem } from '../../form-fields/scope-selector/scope-selector-constants';
import { RevenueShareFormValues } from './form-constants';

export const RevenueShareValidationSchema = yup.object({
  clientId: yup.number().nullable().defined(),
  partnershipId: yup
    .number()
    .nullable()
    .required(
      'Partnership is required. To create a client override also select a client.',
    ),
  scopeableType: yup.string().oneOf(Object.values(ScopeableType)).required(),
  scopes: yup
    .array()
    .of(
      yup.object({
        scopeableType: yup
          .string()
          .oneOf(Object.values(ScopeableType))
          .required(),
        scopeableId: yup.number().nullable().required('Selection required'),
      }),
    )
    .when('scopeableType', ([type], schema) => {
      if (type === ScopeableType.Global) {
        return schema.max(0, 'Global scope cannot include selections');
      }
      return schema
        .min(1, 'Select at least one')
        .test(
          'same-type',
          'All selections must share the same scope type',
          (arr: ScopeItem[] | undefined) =>
            !arr || arr.every(s => s.scopeableType === type),
        );
    }),
  effectiveYear: yup
    .number()
    .nullable()
    .required('Effective year is required')
    .integer('Must be a whole year')
    .min(2020, 'Year must be 2020 or later')
    .max(2099, 'Year must be 2099 or earlier')
    .when('partnershipId', ([partnershipId], schema) => {
      if (partnershipId != null) {
        return schema.test({
          name: 'no-overlap',
          skipAbsent: true,
          test(value, ctx) {
            const { allRevenueShares, editDefinitionId } =
              ctx?.options?.context ?? {};
            const { clientId } = ctx.parent;
            if (!allRevenueShares || value == null) return true;
            const hasOverlap = checkRevenueShareOverlap(
              {
                effectiveYear: value,
                clientId: clientId ?? null,
                partnershipId,
              },
              allRevenueShares,
              editDefinitionId,
            );
            if (hasOverlap) {
              return ctx.createError({
                message: 'A revenue share already exists for this combination',
              });
            }
            return true;
          },
        });
      }
      return schema;
    }),
  discount: yup
    .number()
    .nullable()
    .required('Discount is required')
    .min(1, 'Discount must be at least 1')
    .max(100, 'Discount must be at most 100')
    .test(
      '2 decimals',
      'Discount invalid',
      number => number == null || parseFloat(number.toFixed(2)) === number,
    ),
  applyToPrice: yup.boolean().defined(),
}) as yup.ObjectSchema<RevenueShareFormValues>;
